import React, { useEffect } from 'react';
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxProps
} from '@progress/kendo-react-dropdowns';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Error } from '@progress/kendo-react-labels';
import cloneDeep from 'lodash.clonedeep';
import { DEFAULT_RESOURCE_CATEGORIES } from '../../constants/resource-categories';

const delay: number = 500;

interface ResourceCategoryComboBoxProps extends ComboBoxProps {
  category: string[];
  onRemoveCategory: (categoryName: string) => void;
}

export const ResourceCategoryComboBox = (props: ResourceCategoryComboBoxProps) => {
  const resourceCategories = React.useRef<string[] | undefined>(props.category);
  const [data, setData] = React.useState<string[]>(props.category);
  const [loading, setLoading] = React.useState(false);
  const [filter, setFilter] = React.useState<string>('');

  useEffect(() => {
    setData(props.category);
    resourceCategories.current = props.category;
  }, [props.category]);

  const filterData = (filter: FilterDescriptor) => {
    const data = resourceCategories.current!.slice();
    return filterBy(data, filter);
  };

  const timeout = React.useRef<any>(null);
  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    clearTimeout(timeout.current);

    setFilter(event.filter.value || '');

    timeout.current = setTimeout(() => {
      const filteredData = filterData(event.filter);

      if (
        event.filter.value &&
        !filteredData.some(
          (item) =>
            item.toLowerCase().trim() ===
            event.filter.value.toLowerCase().trim()
        )
      ) {
        setData([event.filter.value, ...filteredData]);
      } else {
        setData(filteredData);
      }

      setLoading(false);
    }, delay);
    setLoading(true);
  };

  const customItemRender = (li: React.ReactElement, itemProps: any) => {
    const index = itemProps.index;
    const itemText = itemProps.dataItem;
    const filteredData = filterData({
      field: undefined,
      operator: 'contains',
      ignoreCase: true,
      value: itemText
    });

    if (
      index === 0 &&
      filter &&
      itemText.trim() === filter.trim() &&
      !filteredData.some(
        (item) => item.toLowerCase().trim() === itemText.toLowerCase().trim()
      )
    ) {
      return React.cloneElement(
        li,
        li.props as React.HTMLAttributes<HTMLLIElement>,
        <div
          className={'w-full'}
          onClick={() => {
            setFilter('');
            if (resourceCategories.current) {
              setData([...resourceCategories.current, filter]);
            }
          }}>
          <span>
            <strong>Add:</strong> {itemText}
          </span>
        </div>
      );
    }

    const isDefault = DEFAULT_RESOURCE_CATEGORIES.some(
      (c) => c.toLowerCase() === itemText.toLowerCase()
    );

    return React.cloneElement(
      li,
      li.props as React.HTMLAttributes<HTMLLIElement>,
      <div className={'w-full d-flex justify-content-between align-items-center'}>
        <span>{itemText}</span>
        {!isDefault && (
          <span
            className={'material-symbols-outlined text-danger'}
            style={{ fontSize: '18px', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              props.onRemoveCategory(itemText);
              const result = cloneDeep(data);
              const dataIndex = result.indexOf(itemText);
              if (dataIndex > -1) {
                result.splice(dataIndex, 1);
              }
              resourceCategories.current = result;
              setData(result);
            }}>
            delete
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <ComboBox
        placeholder={'Select or type to add new category'}
        data={data}
        filterable={true}
        onFilterChange={filterChange}
        loading={loading}
        itemRender={customItemRender}
        {...props}
      />
      {props?.validationMessage && <Error>{props.validationMessage}</Error>}
    </>
  );
};

export default ResourceCategoryComboBox;
